// MoveoutFundsIntakeFormPage — /moveout-funds/:token. PUBLIC, resident-facing.
//
// WEL-48 Move-Out Funds Direction Agreement intake. Every current resident +
// co-signer on the tenancy gets their own tokenized link here. The form asks the
// household to designate ONE recipient for the move-out calculation + remaining
// funds, plus a forwarding mailing address. The FIRST valid completion wins and
// locks the selection — so the page leads with a prominent "coordinate first /
// be confident" notice.
//
// Like IntakeFormPage / VerificationFormPage this is a standalone branded surface
// (NOT wrapped in the authenticated AppShell — see App.tsx) and talks ONLY to the
// public, token-gated /api/moveout-funds/intake/:token endpoints via
// lib/moveoutFundsClient.ts (no Bearer token). All lifecycle state is server-
// driven (view.state): a clean "thanks, it'll be sent for signing" confirmation,
// and a "someone already completed this" locked state.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  buildMoveoutFundsSubmission,
  canSubmitMoveoutFunds,
  getMoveoutFundsIntake,
  MOVEOUT_OTHER_CHOICE,
  cosignerChoice,
  submitMoveoutFundsIntake,
  MoveoutFundsApiError,
  type MoveoutFundsIntakeView,
} from '@/lib/moveoutFundsClient';

// ---------------------------------------------------------------------------
// Brand header (shared lockup with IntakeFormPage, DS tokens)
// ---------------------------------------------------------------------------

function BrandLockup({ propertyAddress }: { propertyAddress: string | null }): React.JSX.Element {
  return (
    <header style={{ textAlign: 'center', marginBottom: 'var(--space-7)' }}>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 26,
          lineHeight: 1.2,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--fg)',
        }}
      >
        Welcome&nbsp;Home
      </div>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 13,
          lineHeight: 1.2,
          letterSpacing: '0.42em',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          paddingLeft: '0.42em',
        }}
      >
        Properties
      </div>
      <div
        style={{
          width: 56,
          height: 1,
          backgroundColor: 'var(--accent)',
          margin: 'var(--space-4) auto 0',
        }}
      />
      {propertyAddress ? (
        <p
          style={{
            marginTop: 'var(--space-4)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--fg-muted)',
          }}
        >
          {propertyAddress}
        </p>
      ) : null}
    </header>
  );
}

function SectionCard(props: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="card" style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h2
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'var(--text-lg)',
            fontWeight: 400,
            color: 'var(--fg)',
            margin: 0,
          }}
        >
          {props.title}
        </h2>
        {props.description ? (
          <p className="modal-description" style={{ marginTop: 'var(--space-2)' }}>
            {props.description}
          </p>
        ) : null}
      </div>
      {props.children}
    </section>
  );
}

function radioTileStyle(selected: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-3) var(--space-4)',
    border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    backgroundColor: selected ? 'var(--accent-soft)' : 'var(--surface)',
  };
}

const OTHER_VALUE = MOVEOUT_OTHER_CHOICE;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MoveoutFundsIntakeFormPage(): React.JSX.Element {
  const { token = '' } = useParams<{ token: string }>();

  const [view, setView] = useState<MoveoutFundsIntakeView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Working copy of the recipient choice. `choice` is a resident tenantId, or
  // OTHER_VALUE for the third-party path.
  const [choice, setChoice] = useState<string>('');
  const [otherName, setOtherName] = useState('');
  const [forwardingAddress, setForwardingAddress] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    getMoveoutFundsIntake(token)
      .then((v) => {
        if (alive) setView(v);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err instanceof MoveoutFundsApiError
            ? 'We could not load this form. The link may have expired.'
            : 'Something went wrong loading this form.',
        );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const isOther = choice === OTHER_VALUE;

  const draft = useMemo(
    () => ({ choice, otherName, forwardingAddress }),
    [choice, otherName, forwardingAddress],
  );
  const canSubmit = useMemo(() => canSubmitMoveoutFunds(draft), [draft]);

  const handleSubmit = useCallback(async () => {
    const body = buildMoveoutFundsSubmission(draft);
    if (!body) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await submitMoveoutFundsIntake(token, body);
      // Merge over the loaded view so the confirmation header keeps premises /
      // propertyAddress / recipientName (the submit result only carries state).
      setView((prev) => (prev ? { ...prev, ...res.view } : res.view));
    } catch (err) {
      setSaveError(
        err instanceof MoveoutFundsApiError
          ? ((err.body as { error?: string } | null)?.error ??
              'We could not save your answer. Please try again.')
          : 'We could not save your answer. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }, [draft, token]);

  const shell = useCallback(
    (children: React.ReactNode): React.JSX.Element => (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--bg-alt)',
          padding: 'var(--space-7) var(--space-4)',
        }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto' }}>{children}</div>
      </div>
    ),
    [],
  );

  // --- Loading / error / terminal states ---
  if (loading) {
    return shell(
      <>
        <BrandLockup propertyAddress={null} />
        <p style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>Loading…</p>
      </>,
    );
  }
  if (loadError || !view) {
    return shell(
      <>
        <BrandLockup propertyAddress={null} />
        <div className="alert" data-tone="warning">
          <div className="alert-body">
            <div className="alert-title">This link is no longer available</div>
            {loadError ?? 'Please reply to the email we sent you and a real person will help.'}
          </div>
        </div>
      </>,
    );
  }

  if (view.state === 'unavailable') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress} />
        <SectionCard
          title="Nothing to fill out right now"
          description="This link is valid, but there's nothing for you to provide at the moment. If we need something from you, we'll send a fresh link."
        >
          <></>
        </SectionCard>
      </>,
    );
  }

  if (view.state === 'cancelled') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress} />
        <SectionCard
          title="This request has been canceled"
          description="There's nothing for you to fill out here — this request was canceled and no action is needed. If we need anything from you, we'll send a fresh link."
        >
          <></>
        </SectionCard>
      </>,
    );
  }

  // Someone in the household completed this first — the selection is locked.
  if (view.state === 'locked') {
    const sel = view.lockedSelection;
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress} />
        <SectionCard
          title="Already taken care of"
          description="Someone in your household has already completed this — so there's nothing more for you to do."
        >
          {sel ? (
            <div className="alert" data-tone="info">
              <div className="alert-body">
                <strong>{sel.designatedRecipientName}</strong> was designated to receive the
                move-out calculation and any remaining funds
                {sel.completedByName ? ` (submitted by ${sel.completedByName})` : ''}. The agreement
                will be sent to everyone to sign.
              </div>
            </div>
          ) : (
            <p style={{ color: 'var(--fg-muted)', margin: 0 }}>
              The agreement will be sent to everyone to sign. If this doesn&apos;t look right, reply
              to the email we sent you and we&apos;ll help.
            </p>
          )}
        </SectionCard>
      </>,
    );
  }

  // This party just submitted — clean confirmation.
  if (view.state === 'submitted') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress} />
        <div className="card" data-tone="success" style={{ textAlign: 'center' }}>
          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 28,
              fontWeight: 400,
              margin: 0,
              color: 'var(--fg)',
            }}
          >
            Thank you — that&apos;s all we need.
          </h1>
          <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
            We&apos;ve recorded who should receive the move-out calculation and any remaining funds.
            The Move-Out Funds Direction Agreement will be emailed to everyone to sign shortly.
          </p>
        </div>
      </>,
    );
  }

  // --- Fillable form ---
  const greetingName = view.recipientName ? `, ${view.recipientName.split(/\s+/)[0]}` : '';

  return shell(
    <>
      <BrandLockup propertyAddress={view.propertyAddress} />
      <div style={{ marginBottom: 'var(--space-6)', textAlign: 'center' }}>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 26,
            fontWeight: 400,
            margin: 0,
            color: 'var(--fg)',
          }}
        >
          Move-Out Funds Direction{greetingName}
        </h1>
        <p style={{ marginTop: 'var(--space-3)', color: 'var(--fg-muted)' }}>
          As you move out{view.premises ? ` of ${view.premises}` : ''}, please tell us who should
          receive the move-out calculation and any remaining funds (including the deposit).
        </p>
      </div>

      {/* First-completion-wins notice — prominent, before the inputs. */}
      <div className="alert" data-tone="warning" style={{ marginBottom: 'var(--space-5)' }}>
        <div className="alert-body">
          <div className="alert-title">Please coordinate before you submit</div>
          <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)' }}>
            Everyone on the lease received this same link. <strong>The first response we receive is
            the one we use</strong> — it can&apos;t be changed here afterward. Talk it over with the
            others and only submit once you&apos;re all confident in the choice.
          </p>
        </div>
      </div>

      {saveError ? (
        <div className="alert" data-tone="danger" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">{saveError}</div>
        </div>
      ) : null}

      <SectionCard
        title="Who should receive the funds?"
        description="Choose one person to receive the move-out calculation and any remaining funds on behalf of everyone."
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {view.residents.map((r) => (
            <label key={r.tenantId} style={radioTileStyle(choice === r.tenantId)}>
              <input
                type="radio"
                name="recipient"
                checked={choice === r.tenantId}
                onChange={() => setChoice(r.tenantId)}
              />
              <span>{r.name}</span>
            </label>
          ))}
          {/* WEL-143 — co-signers are pickable. Their capacity is shown so the
              chooser knows who they are looking at, and so a co-signer is never
              re-entered by hand as "someone else". */}
          {view.cosigners.map((c) => {
            const value = cosignerChoice(c.tenantId);
            return (
              <label key={value} style={radioTileStyle(choice === value)}>
                <input
                  type="radio"
                  name="recipient"
                  checked={choice === value}
                  onChange={() => setChoice(value)}
                />
                <span>
                  {c.name}{' '}
                  <span style={{ color: 'var(--text-muted)' }}>(co-signer)</span>
                </span>
              </label>
            );
          })}
          <label style={radioTileStyle(isOther)}>
            <input
              type="radio"
              name="recipient"
              checked={isOther}
              onChange={() => setChoice(OTHER_VALUE)}
            />
            <span>Someone else (not listed above)</span>
          </label>
        </div>

        {isOther ? (
          <div
            style={{
              marginTop: 'var(--space-4)',
              display: 'grid',
              gap: 'var(--space-3)',
            }}
          >
            <div className="field">
              <label>Recipient&apos;s full name</label>
              <input
                className="input"
                value={otherName}
                onChange={(e) => setOtherName(e.target.value)}
                placeholder="Full name"
              />
              <span className="helper">
                They don&apos;t need to sign — the funds and the move-out
                calculation go to the mailing address below.
              </span>
            </div>
          </div>
        ) : null}
      </SectionCard>

      <SectionCard
        title="Forwarding mailing address"
        description="Where should we mail the move-out calculation and any remaining funds for the recipient?"
      >
        <div className="field" style={{ margin: 0 }}>
          <label htmlFor="mofd-forwarding">Mailing address</label>
          <textarea
            id="mofd-forwarding"
            className="input"
            rows={3}
            value={forwardingAddress}
            onChange={(e) => setForwardingAddress(e.target.value)}
            placeholder="Street, city, state, ZIP"
          />
        </div>
      </SectionCard>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginTop: 'var(--space-6)',
        }}
      >
        {!canSubmit ? (
          <span className="helper" style={{ color: 'var(--fg-muted)' }}>
            Choose a recipient and enter a forwarding address to submit.
          </span>
        ) : null}
        <button
          className="btn"
          data-size="lg"
          disabled={saving || !canSubmit}
          onClick={() => void handleSubmit()}
        >
          {saving ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </>,
  );
}
